import {
	AlertTriangle,
	ArrowRight,
	Box,
	Building2,
	CheckCircle2,
	Clock,
	Download,
	Loader2,
	Lock,
	Mail,
	MapPin,
	Phone,
	Server,
	XCircle,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "../../api/client";
import CodeLivePreview from "../../components/CodeLivePreview";
import {
	AcceptModal,
	RejectModal,
} from "../../components/ProposalActionModals";
import SectionRenderer from "../../components/SectionRenderer";
import { DEFAULT_ACCEPTANCE_CONFIG } from "../../constants/proposalDefaults";
import { ICON_MAP, ITEM_THEMES, THEME_INDEX_MAP } from "../../constants/themes";
import {
	calcTotal,
	getExpirationInfo,
	parseDescription,
	parseJsonField,
	getItemLayout as resolveItemLayout,
	getItemTheme as resolveItemTheme,
} from "../../utils/proposalHelpers";

export default function ProposalView() {
	const { token } = useParams();
	const [proposal, setProposal] = useState(null);
	const [items, setItems] = useState([]);
	const [settings, setSettings] = useState({});
	const [proposalResponse, setProposalResponse] = useState(null);
	const [accessToken, setAccessToken] = useState(token);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [showAcceptModal, setShowAcceptModal] = useState(false);
	const [showRejectModal, setShowRejectModal] = useState(false);
	const [acceptLoading, setAcceptLoading] = useState(false);
	const [rejectLoading, setRejectLoading] = useState(false);
	const [submitSuccess, setSubmitSuccess] = useState(null);
	const [formData, setFormData] = useState({
		approver_name: "",
		approver_role: "",
		approver_email: "",
		approver_phone: "",
	});
	const [rejectData, setRejectData] = useState({
		rejection_reason: "",
		rejection_observations: "",
	});
	const [formErrors, setFormErrors] = useState({});
	const [rejectErrors, setRejectErrors] = useState({});
	const [expiresAt, setExpiresAt] = useState(null);
	const [isExpired, setIsExpired] = useState(false);
	const [rejectionReasons, setRejectionReasons] = useState([]);

	// Controle de Senha/PIN de Acesso
	const [passcodeRequired, setPasscodeRequired] = useState(false);
	const [passcode, setPasscode] = useState(
		sessionStorage.getItem(`passcode_${token}`) || "",
	);
	const [passcodeError, setPasscodeError] = useState("");

	const loadProposal = useCallback(
		async (providedPasscode) => {
			try {
				setLoading(true);
				setError(null);
				const activePasscode =
					providedPasscode !== undefined ? providedPasscode : passcode;
				const headers = activePasscode
					? { "x-proposal-passcode": activePasscode }
					: {};

				const [response, reasonsRes] = await Promise.all([
					api.get(`/proposals/token/${token}`, { headers }),
					api.get("/rejection-reasons/active").catch(() => ({ data: [] })),
				]);

				if (response.data.passcode_required) {
					setPasscodeRequired(true);
					setProposal(response.data.proposal);
					setSettings(response.data.settings || {});
					if (providedPasscode !== undefined && providedPasscode !== "") {
						setPasscodeError("Código de acesso inválido.");
					}
				} else {
					setPasscodeRequired(false);
					setPasscodeError("");
					setProposal(response.data.proposal);
					setItems(response.data.items || []);
					setSettings(response.data.settings || {});
					setProposalResponse(response.data.response || null);
					setAccessToken(response.data.token || token);
					setExpiresAt(response.data.expires_at || null);
					setIsExpired(response.data.is_expired || false);
					if (activePasscode) {
						sessionStorage.setItem(`passcode_${token}`, activePasscode);
					}
				}
				setRejectionReasons(reasonsRes.data || []);
			} catch (err) {
				if (err.response?.status === 401) {
					setError(
						"Este link expirou ou e invalido. Solicite um novo link ao responsavel.",
					);
				} else {
					setError(err.response?.data?.error || "Erro ao carregar proposta");
				}
			} finally {
				setLoading(false);
			}
		},
		[token, passcode],
	);

	useEffect(() => {
		if (token) loadProposal();
	}, [token, loadProposal]);

	const handlePasscodeSubmit = (e) => {
		e.preventDefault();
		if (!passcode.trim()) {
			setPasscodeError("Por favor, digite o código de acesso.");
			return;
		}
		loadProposal(passcode);
	};

	const handleAccept = async () => {
		const errors = {};
		if (!formData.approver_name.trim())
			errors.approver_name = "Nome e obrigatorio";
		if (!formData.approver_email.trim())
			errors.approver_email = "Email e obrigatorio";
		else if (!formData.approver_email.includes("@"))
			errors.approver_email = "Email invalido";
		if (Object.keys(errors).length > 0) {
			setFormErrors(errors);
			return;
		}

		try {
			setAcceptLoading(true);
			const headers = passcode ? { "x-proposal-passcode": passcode } : {};
			await api.post(
				`/proposals/${proposal.id}/accept`,
				{
					token: accessToken,
					passcode,
					...formData,
				},
				{ headers },
			);
			setSubmitSuccess("accepted");
			setShowAcceptModal(false);
			setTimeout(() => loadProposal(), 1500);
		} catch (err) {
			setFormErrors({
				general: err.response?.data?.error || "Erro ao aceitar",
			});
		} finally {
			setAcceptLoading(false);
		}
	};

	const handleReject = async () => {
		const errors = {};
		if (!rejectData.rejection_reason)
			errors.rejection_reason = "Selecione um motivo";
		if (Object.keys(errors).length > 0) {
			setRejectErrors(errors);
			return;
		}

		try {
			setRejectLoading(true);
			const headers = passcode ? { "x-proposal-passcode": passcode } : {};
			await api.post(
				`/proposals/${proposal.id}/reject`,
				{
					token: accessToken,
					passcode,
					approver_name: formData.approver_name || "Nao informado",
					approver_email: formData.approver_email || "",
					...rejectData,
				},
				{ headers },
			);
			setSubmitSuccess("rejected");
			setShowRejectModal(false);
			setTimeout(() => loadProposal(), 1500);
		} catch (err) {
			setRejectErrors({
				general: err.response?.data?.error || "Erro ao rejeitar",
			});
		} finally {
			setRejectLoading(false);
		}
	};

	if (loading) {
		return (
			<div className="min-h-screen bg-[#0B0F19] flex items-center justify-center">
				<div className="text-center">
					<Loader2 className="w-12 h-12 animate-spin text-cyan-400 mx-auto mb-4" />
					<p className="text-slate-300 font-medium">Carregando proposta...</p>
				</div>
			</div>
		);
	}

	if (error) {
		return (
			<div className="min-h-screen bg-[#0B0F19] flex items-center justify-center p-4">
				<div className="max-w-md bg-[#131620] border border-red-500/20 rounded-2xl p-10 text-center shadow-2xl">
					<div className="w-20 h-20 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
						<AlertTriangle className="w-10 h-10 text-red-400" />
					</div>
					<h1 className="text-2xl font-bold text-white mb-3">Link Invalido</h1>
					<p className="text-slate-400 leading-relaxed">{error}</p>
				</div>
			</div>
		);
	}

	if (passcodeRequired) {
		const companyName = proposal?.company_name || "Empresa";
		const companyLogo = proposal?.company_logo || settings.app_logo_url;

		return (
			<div className="min-h-screen bg-[#0B0F19] text-slate-300 font-sans flex items-center justify-center p-4">
				<div className="absolute top-0 right-0 w-[400px] h-[400px] bg-cyan-500/10 rounded-full blur-[100px] -z-10"></div>
				<div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-purple-600/10 rounded-full blur-[100px] -z-10"></div>

				<div className="w-full max-w-md bg-[#131620]/80 backdrop-blur-xl border border-slate-800 rounded-3xl p-8 md:p-10 shadow-2xl relative animate-in fade-in zoom-in duration-300">
					<div className="text-center mb-8">
						{companyLogo ? (
							<img
								src={companyLogo}
								alt={companyName}
								className="h-12 object-contain mx-auto mb-6 animate-pulse"
								onError={(e) => {
									e.target.style.display = "none";
								}}
							/>
						) : (
							<div className="h-16 w-16 bg-gradient-to-br from-cyan-500 to-purple-600 rounded-2xl flex items-center justify-center font-bold text-white shadow-lg mx-auto mb-6 text-xl">
								{companyName.charAt(0)}
							</div>
						)}
						<h2 className="text-2xl font-bold text-white mb-2">
							Acesso Protegido
						</h2>
						<p className="text-slate-400 text-sm">
							Esta proposta para{" "}
							<span className="text-white font-semibold">
								{proposal?.client_name || "você"}
							</span>{" "}
							exige um código de segurança.
						</p>
					</div>

					<form onSubmit={handlePasscodeSubmit} className="space-y-6">
						<div>
							<label
								htmlFor="passcode"
								className="block text-xs font-bold uppercase tracking-wider text-slate-400 mb-2"
							>
								Código de Acesso
							</label>
							<div className="relative">
								<input
									id="passcode"
									type="password"
									value={passcode}
									onChange={(e) => setPasscode(e.target.value)}
									placeholder="Digite o código ou PIN"
									className="w-full bg-slate-900/50 border border-slate-800 rounded-xl py-3.5 px-4 text-white placeholder-slate-600 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-all font-mono"
								/>
								<Lock className="absolute right-4 top-4 text-slate-600 w-5 h-5" />
							</div>
							{passcodeError && (
								<p className="text-red-400 text-xs mt-2 flex items-center gap-1 font-medium">
									<AlertTriangle className="w-3.5 h-3.5" />
									{passcodeError}
								</p>
							)}
						</div>

						<button
							type="submit"
							className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-bold py-3.5 px-6 rounded-xl shadow-lg shadow-cyan-500/20 hover:shadow-cyan-500/30 transition-all flex items-center justify-center gap-2 cursor-pointer"
						>
							Acessar Proposta <ArrowRight size={18} />
						</button>
					</form>
				</div>
			</div>
		);
	}

	if (!proposal) return null;

	const total = calcTotal(items);
	const isResponded = proposal.status !== "PENDING";
	const sections = parseJsonField(proposal.sections, []);
	const companyName = proposal.company_name || "Empresa";
	const companyLogo = proposal.company_logo || settings.app_logo_url;
	const companyTagline = proposal.company_tagline;
	const companyDescription =
		proposal.company_description || proposal.description;

	const templateData = parseJsonField(proposal.template_data, {});
	const acceptanceConfig = parseJsonField(proposal.acceptance_config, {
		...DEFAULT_ACCEPTANCE_CONFIG,
	});

	const theme = templateData.theme || {};
	const variables = {
		...(templateData.variables || {}),
		client_name: proposal.client_name,
		total_value: `R$ ${total.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`,
		proposal_id: proposal.proposal_number,
	};

	const getItemTheme = (item, index) =>
		resolveItemTheme(item, index, ITEM_THEMES, THEME_INDEX_MAP);
	const getItemLayout = (item, index) => resolveItemLayout(item, index);
	const compactItems = items.filter(
		(item, i) => getItemLayout(item, i) === "compact-card",
	);
	const mainItems = items.filter(
		(item, i) => getItemLayout(item, i) !== "compact-card",
	);
	const expirationInfo = getExpirationInfo(expiresAt, isExpired);

	const hideActions =
		isExpired ||
		(expirationInfo && expirationInfo.type === "expired") ||
		!acceptanceConfig.show_acceptance;
	const handlePrintPDF = () => {
		window.print();
	};

	const _interpolate = (text) => {
		if (!text) return "";
		return text
			.replace(/\{\{company_name\}\}/g, proposal.company_name || "")
			.replace(/\{\{company_legal_name\}\}/g, proposal.company_legal_name || "")
			.replace(
				/\{\{total\}\}/g,
				`R$ ${total.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`,
			)
			.replace(/\{\{items_count\}\}/g, String(items.length))
			.replace(/\{\{client_name\}\}/g, proposal.client_name || "");
	};

	// If we have a snapshot, we use the Sandbox Render
	if (proposal.snapshot_code) {
		return (
			<div className="min-h-screen bg-[#0B0F19]">
				<style>{`@media print { .print-hide { display: none !important; } }`}</style>

				{/* Floating Actions for Snapshot View */}
				<div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex gap-4 print-hide">
					{!isResponded && !hideActions && (
						<button
							type="button"
							onClick={() => setShowAcceptModal(true)}
							className="bg-green-600 hover:bg-green-500 text-white font-bold py-3 px-8 rounded-full shadow-2xl transition-all transform hover:scale-105 flex items-center gap-2"
						>
							<CheckCircle2 size={20} /> Aprovar Proposta
						</button>
					)}
					<button
						type="button"
						onClick={handlePrintPDF}
						className="bg-slate-800/80 backdrop-blur hover:bg-slate-700 text-white py-3 px-6 rounded-full border border-white/10 shadow-xl transition-all flex items-center gap-2"
					>
						<Download size={18} /> PDF
					</button>
				</div>

				{/* Success Toast / Badges */}
				{isResponded && (
					<div className="fixed top-6 right-6 z-50">
						<div
							className={`p-4 rounded-xl shadow-2xl flex items-center gap-3 ${proposal.status === "ACCEPTED" ? "bg-green-600/20 border border-green-500/30 text-green-300" : "bg-red-600/20 border border-red-500/30 text-red-300"} backdrop-blur-xl`}
						>
							{proposal.status === "ACCEPTED" ? (
								<CheckCircle2 size={20} />
							) : (
								<XCircle size={20} />
							)}
							Proposta {proposal.status === "ACCEPTED" ? "Aceita" : "Rejeitada"}
						</div>
					</div>
				)}

				<div className="w-full min-h-screen">
					<CodeLivePreview
						code={proposal.snapshot_code}
						theme={theme}
						variables={variables}
						height="100vh"
						codeType={templateData.code_type || "react"}
					/>
				</div>

				{/* Modal mounts */}
				<AcceptModal
					isOpen={showAcceptModal}
					onClose={() => setShowAcceptModal(false)}
					onAccept={handleAccept}
					formData={formData}
					setFormData={setFormData}
					formErrors={formErrors}
					setFormErrors={setFormErrors}
					loading={acceptLoading}
					proposalNumber={proposal.proposal_number}
					totalValue={total.toLocaleString("pt-BR", {
						minimumFractionDigits: 2,
					})}
					acceptanceConfig={acceptanceConfig}
				/>
				<RejectModal
					isOpen={showRejectModal}
					onClose={() => setShowRejectModal(false)}
					onReject={handleReject}
					rejectData={rejectData}
					setRejectData={setRejectData}
					rejectErrors={rejectErrors}
					setRejectErrors={setRejectErrors}
					loading={rejectLoading}
					rejectionReasons={rejectionReasons}
				/>
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-[#0B0F19] text-slate-300 font-sans selection:bg-cyan-500/30">
			{/* Print-specific styles for PDF export */}
			<style>{`
        @media print {
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          nav, .print-hide { display: none !important; }
          section { break-inside: avoid; }
        }
      `}</style>

			{/* Success Toast */}
			{submitSuccess && (
				<div
					className={`fixed top-6 right-6 z-50 p-4 rounded-xl shadow-2xl flex items-center gap-3 ${
						submitSuccess === "accepted"
							? "bg-green-600/20 border border-green-500/30 text-green-300"
							: "bg-red-600/20 border border-red-500/30 text-red-300"
					} backdrop-blur-xl`}
				>
					{submitSuccess === "accepted" ? (
						<CheckCircle2 className="w-5 h-5" />
					) : (
						<XCircle className="w-5 h-5" />
					)}
					Proposta {submitSuccess === "accepted" ? "aceita" : "rejeitada"} com
					sucesso!
				</div>
			)}

			{/* ========== NAV ========== */}
			<nav className="fixed top-0 w-full z-50 bg-[#0B0F19]/90 backdrop-blur-md border-b border-white/5">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 h-20 flex items-center justify-between">
					<div className="flex items-center gap-3">
						{companyLogo ? (
							<img
								src={companyLogo}
								alt={companyName}
								className="h-8 object-contain"
								onError={(e) => {
									e.target.style.display = "none";
								}}
							/>
						) : (
							<div className="h-10 w-10 bg-gradient-to-br from-cyan-500 to-purple-600 rounded-lg flex items-center justify-center font-bold text-white shadow-lg">
								{companyName.charAt(0)}
							</div>
						)}
						<span className="text-lg sm:text-xl font-bold text-white tracking-tight">
							{companyName}
						</span>
					</div>
					<div className="flex items-center gap-4 sm:gap-6">
						<div className="text-right">
							<p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
								Preparado para
							</p>
							<p className="text-xs sm:text-sm font-semibold text-white truncate max-w-[120px] sm:max-w-none">
								{proposal.client_name}
							</p>
						</div>
						{!isResponded && !hideActions && (
							<button
								type="button"
								onClick={() => setShowAcceptModal(true)}
								className="print-hide hidden sm:block bg-white/5 hover:bg-white/10 text-white border border-white/10 px-4 py-2 rounded-lg text-sm font-medium transition-all"
							>
								Aprovar Proposta
							</button>
						)}
					</div>
				</div>
			</nav>

			{/* ========== EXPIRATION BADGE ========== */}
			{expirationInfo && expirationInfo.type === "expired" && (
				<div className="print-hide fixed top-20 left-0 w-full z-40 bg-red-600/90 backdrop-blur-sm text-white text-center py-3 px-4 font-semibold text-sm flex items-center justify-center gap-2 shadow-lg">
					<AlertTriangle size={18} />
					Esta proposta expirou
				</div>
			)}

			{/* ========== EXPIRATION COUNTDOWN + PDF BUTTON ========== */}
			<div
				className={`pt-24 ${expirationInfo && expirationInfo.type === "expired" ? "mt-12" : ""}`}
			>
				<div className="max-w-7xl mx-auto px-6 flex flex-wrap items-center justify-between gap-3 py-3">
					<div className="flex items-center gap-3">
						{expirationInfo && expirationInfo.type !== "expired" && (
							<div
								className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold border ${
									expirationInfo.type === "urgent"
										? "bg-yellow-500/10 border-yellow-500/20 text-yellow-400"
										: expirationInfo.type === "warning"
											? "bg-amber-500/10 border-amber-500/20 text-amber-400"
											: "bg-green-500/10 border-green-500/20 text-green-400"
								}`}
							>
								<Clock size={14} />
								{expirationInfo.type === "urgent" ||
								expirationInfo.type === "warning"
									? `Expira em ${expirationInfo.daysLeft} dia${expirationInfo.daysLeft !== 1 ? "s" : ""}`
									: `Valida por mais ${expirationInfo.daysLeft} dia${expirationInfo.daysLeft !== 1 ? "s" : ""}`}
							</div>
						)}
					</div>
					<button
						type="button"
						onClick={handlePrintPDF}
						className="print-hide inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-white text-sm font-medium transition-all"
					>
						<Download size={16} />
						Baixar PDF
					</button>
				</div>
			</div>

			<AcceptModal
				isOpen={showAcceptModal}
				onClose={() => setShowAcceptModal(false)}
				onAccept={handleAccept}
				formData={formData}
				setFormData={setFormData}
				formErrors={formErrors}
				setFormErrors={setFormErrors}
				loading={acceptLoading}
				proposalNumber={proposal.proposal_number}
				totalValue={total.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
				acceptanceConfig={acceptanceConfig}
			/>
			<RejectModal
				isOpen={showRejectModal}
				onClose={() => setShowRejectModal(false)}
				onReject={handleReject}
				rejectData={rejectData}
				setRejectData={setRejectData}
				rejectErrors={rejectErrors}
				setRejectErrors={setRejectErrors}
				loading={rejectLoading}
				rejectionReasons={rejectionReasons}
			/>

			{/* ========== HERO EMPRESA ========== */}
			<section className="pt-8 pb-20 relative overflow-hidden">
				<div className="absolute top-0 right-0 w-[600px] h-[600px] bg-cyan-500/10 rounded-full blur-[120px] -z-10"></div>
				<div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-purple-600/10 rounded-full blur-[120px] -z-10"></div>

				<div className="max-w-7xl mx-auto px-6">
					{/* Status Banner (if responded) */}
					{isResponded && (
						<div
							className={`mb-8 inline-flex items-center gap-3 px-6 py-3 rounded-xl ${
								proposal.status === "ACCEPTED"
									? "bg-green-500/10 border border-green-500/20 text-green-400"
									: "bg-red-500/10 border border-red-500/20 text-red-400"
							}`}
						>
							{proposal.status === "ACCEPTED" ? (
								<CheckCircle2 className="w-6 h-6" />
							) : (
								<XCircle className="w-6 h-6" />
							)}
							<div>
								<p className="font-bold text-lg">
									Proposta{" "}
									{proposal.status === "ACCEPTED" ? "Aceita" : "Rejeitada"}
								</p>
								{proposal.responded_at && (
									<p className="text-sm opacity-70">
										em{" "}
										{new Date(proposal.responded_at).toLocaleDateString(
											"pt-BR",
										)}
									</p>
								)}
								{proposalResponse?.approver_name && (
									<p className="text-sm opacity-70">
										por {proposalResponse.approver_name}
									</p>
								)}
							</div>
						</div>
					)}

					{proposal.status === "REJECTED" && proposalResponse && (
						<div className="mb-8 bg-red-500/5 border border-red-500/10 rounded-xl p-6">
							<p className="text-red-300 font-medium mb-1">
								Motivo: {proposalResponse.rejection_reason}
							</p>
							{proposalResponse.rejection_observations && (
								<p className="text-red-200/60 text-sm">
									{proposalResponse.rejection_observations}
								</p>
							)}
						</div>
					)}

					<div className="grid lg:grid-cols-2 gap-16 items-center">
						<div className="space-y-8">
							<div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-xs font-bold uppercase tracking-wide">
								<span className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse"></span>
								{companyTagline || "Proposta Comercial"}
							</div>

							<h1 className="text-4xl md:text-5xl font-bold text-white leading-[1.1]">
								{proposal.title || (
									<>
										{companyName} <br />
										<span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-500">
											ao seu servico.
										</span>
									</>
								)}
							</h1>

							{companyDescription && (
								<p className="text-lg text-slate-400 leading-relaxed max-w-xl">
									{companyDescription}
								</p>
							)}

							{(() => {
								const heroCards =
									proposal.hero_cards?.length > 0
										? typeof proposal.hero_cards === "string"
											? JSON.parse(proposal.hero_cards)
											: proposal.hero_cards
										: [
												{
													icon: "building2",
													color: "cyan",
													title: "{{company_name}}",
													subtitle: "{{company_legal_name}}",
												},
												{
													icon: "shield",
													color: "purple",
													title: "{{total}}",
													subtitle: "Investimento mensal",
												},
											];
								const interpolate = (text) => {
									if (!text) return "";
									return text
										.replace(
											/\{\{company_name\}\}/g,
											proposal.company_name || "",
										)
										.replace(
											/\{\{company_legal_name\}\}/g,
											proposal.company_legal_name || "",
										)
										.replace(
											/\{\{total\}\}/g,
											`R$ ${total.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`,
										)
										.replace(/\{\{items_count\}\}/g, String(items.length))
										.replace(
											/\{\{client_name\}\}/g,
											proposal.client_name || "",
										);
								};
								const colorMap = {
									cyan: "text-cyan-400",
									purple: "text-purple-400",
									emerald: "text-emerald-400",
									blue: "text-blue-400",
									amber: "text-amber-400",
								};
								const cols = heroCards.length > 3 ? 4 : heroCards.length;
								return (
									<div
										className={`grid gap-4 pt-4`}
										style={{
											gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
										}}
									>
										{heroCards.map((card, i) => {
											const CardIcon = ICON_MAP[card.icon] || Building2;
											return (
												<div
													key={i}
													className="p-4 rounded-xl bg-slate-900/80 border border-slate-800"
												>
													<CardIcon
														className={`${colorMap[card.color] || "text-cyan-400"} mb-3`}
														size={24}
													/>
													<h3 className="text-white font-semibold text-sm">
														{interpolate(card.title)}
													</h3>
													<p className="text-xs text-slate-500">
														{interpolate(card.subtitle)}
													</p>
												</div>
											);
										})}
									</div>
								);
							})()}
						</div>

						{/* Visual Terminal Card */}
						<div className="relative hidden lg:block">
							<div className="absolute inset-0 bg-gradient-to-r from-cyan-500 to-purple-600 rounded-2xl blur-2xl opacity-20 transform rotate-3"></div>
							<div className="relative bg-[#131620] border border-slate-800 rounded-2xl overflow-hidden shadow-2xl">
								<div className="bg-[#0f1119] px-4 py-3 border-b border-slate-800 flex items-center gap-2">
									<div className="flex gap-1.5">
										<div className="w-3 h-3 rounded-full bg-red-500/20 border border-red-500/50"></div>
										<div className="w-3 h-3 rounded-full bg-yellow-500/20 border border-yellow-500/50"></div>
										<div className="w-3 h-3 rounded-full bg-green-500/20 border border-green-500/50"></div>
									</div>
									<div className="text-xs text-slate-500 font-mono ml-2">
										proposta-detalhes
									</div>
								</div>
								<div className="p-6 font-mono text-sm space-y-4">
									<div className="flex justify-between items-center text-xs text-slate-500 uppercase tracking-wider mb-4">
										<span>Resumo</span>
										<span className="text-green-400 flex items-center gap-1">
											<span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse"></span>
											{items.length} {items.length === 1 ? "item" : "itens"}
										</span>
									</div>
									{items.slice(0, 4).map((item, i) => (
										<div
											key={i}
											className="flex justify-between text-slate-400 text-xs"
										>
											<span className="truncate mr-4">
												{item.name || `Item ${i + 1}`}
											</span>
											<span className="text-cyan-400 font-bold whitespace-nowrap">
												R${" "}
												{parseFloat(item.value).toLocaleString("pt-BR", {
													minimumFractionDigits: 2,
												})}
											</span>
										</div>
									))}
									{items.length > 4 && (
										<p className="text-xs text-slate-600">
											+ {items.length - 4} mais itens...
										</p>
									)}
									<div className="pt-4 border-t border-slate-800">
										<div className="flex justify-between">
											<span className="text-slate-400 text-xs">
												Total Mensal
											</span>
											<span className="text-white font-bold">
												R${" "}
												{total.toLocaleString("pt-BR", {
													minimumFractionDigits: 2,
												})}
											</span>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</section>

			{/* ========== CUSTOM SECTIONS ========== */}
			{sections.length > 0 &&
				sections.map((section, idx) => (
					<SectionRenderer
						key={idx}
						section={section}
						index={idx}
						company={{
							name: companyName,
							tagline: companyTagline,
							description: companyDescription,
						}}
					/>
				))}

			{/* ========== ITEMS - LAYOUT AWARE ========== */}
			{mainItems.map((item, _idx) => {
				const globalIdx = items.indexOf(item);
				const theme = getItemTheme(item, globalIdx);
				const layout = getItemLayout(item, globalIdx);
				const ItemIcon =
					ICON_MAP[item.icon] || (item.item_type === "plan" ? Server : Box);
				const { mainText, bullets } = parseDescription(item.description);
				const itemTotal = parseFloat(item.value) * (item.quantity || 1);
				const itemFeatures =
					Array.isArray(item.features) && item.features.length > 0
						? item.features
						: null;
				const subtitleText =
					item.subtitle || (item.item_type === "plan" ? "Plano" : "Modulo");

				const renderFeatures = (gridMode = false) => {
					if (itemFeatures) {
						return itemFeatures.map((feat, fi) => {
							const FeatIcon = ICON_MAP[feat.icon] || CheckCircle2;
							return (
								<div
									key={fi}
									className={
										gridMode
											? `p-4 rounded-xl border ${theme.featureBg} flex items-start gap-3`
											: "flex items-start gap-3"
									}
								>
									<FeatIcon
										className={`w-5 h-5 mt-0.5 flex-shrink-0 ${theme.featureIcon}`}
									/>
									<div>
										{feat.title && (
											<p className="text-sm font-semibold text-white mb-0.5">
												{feat.title}
											</p>
										)}
										<span className="text-sm text-slate-300">
											{feat.description}
										</span>
									</div>
								</div>
							);
						});
					}
					if (bullets.length > 0) {
						return bullets.map((bullet, bi) => (
							<div
								key={bi}
								className={
									gridMode
										? `p-4 rounded-xl border ${theme.featureBg} flex items-start gap-3`
										: "flex items-start gap-3"
								}
							>
								<CheckCircle2
									className={`w-5 h-5 mt-0.5 flex-shrink-0 ${theme.featureIcon}`}
								/>
								<span className="text-sm text-slate-300">{bullet}</span>
							</div>
						));
					}
					return null;
				};

				if (layout === "full-width") {
					const featureItems = renderFeatures(true);
					return (
						<section
							key={`item-${globalIdx}`}
							className="py-24 bg-[#0F121C] border-y border-white/5 relative overflow-hidden"
						>
							<div
								className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] ${theme.glow} rounded-full blur-[150px] opacity-30 -z-10`}
							></div>
							<div className="max-w-6xl mx-auto px-6">
								<div className="text-center mb-12">
									<div
										className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border text-xs font-bold uppercase tracking-wide mb-6 ${theme.badge}`}
									>
										<ItemIcon size={14} />
										{subtitleText}
									</div>
									<h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
										{item.name || `Item ${globalIdx + 1}`}
									</h2>
									{mainText && (
										<p className="text-lg text-slate-400 leading-relaxed max-w-3xl mx-auto">
											{mainText}
										</p>
									)}
								</div>

								{featureItems && (
									<div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-12 max-w-4xl mx-auto">
										{featureItems}
									</div>
								)}

								<div
									className={`max-w-2xl mx-auto p-6 rounded-2xl border ${theme.pricingBg} flex items-center justify-between`}
								>
									<div className="flex items-center gap-4">
										<div
											className={`w-12 h-12 rounded-xl ${theme.iconBg} flex items-center justify-center`}
										>
											<ItemIcon className={theme.icon} size={24} />
										</div>
										<div>
											<p className="text-white font-semibold">
												{item.name || `Item ${globalIdx + 1}`}
											</p>
											<p className="text-xs text-slate-500">
												{subtitleText}
												{item.quantity > 1 && ` x${item.quantity}`}
											</p>
										</div>
									</div>
									<div className="text-right">
										<p className={`text-3xl font-bold ${theme.priceText}`}>
											R${" "}
											{itemTotal.toLocaleString("pt-BR", {
												minimumFractionDigits: 2,
											})}
										</p>
										<p className="text-xs text-slate-500">/mes</p>
									</div>
								</div>
							</div>
						</section>
					);
				}

				const featureItemsList = renderFeatures(false);
				return (
					<section
						key={`item-${globalIdx}`}
						className="py-24 relative overflow-hidden"
					>
						<div
							className={`absolute top-0 right-0 w-[400px] h-[400px] ${theme.glow} rounded-full blur-[120px] opacity-20 -z-10`}
						></div>
						<div className="max-w-6xl mx-auto px-6">
							<div className="grid lg:grid-cols-2 gap-16 items-center">
								<div className="space-y-6">
									<div
										className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border text-xs font-bold uppercase tracking-wide ${theme.badge}`}
									>
										<ItemIcon size={14} />
										{subtitleText}
									</div>

									<h2 className="text-3xl md:text-4xl font-bold text-white">
										{item.name || `Item ${globalIdx + 1}`}
									</h2>

									{mainText && (
										<p className="text-lg text-slate-400 leading-relaxed">
											{mainText}
										</p>
									)}

									{featureItemsList && (
										<div className="space-y-3 pt-2">{featureItemsList}</div>
									)}

									<div
										className={`p-6 rounded-2xl border ${theme.pricingBg} flex items-center justify-between mt-6`}
									>
										<div>
											<p className="text-sm text-slate-500">
												{subtitleText}
												{item.quantity > 1 && ` x${item.quantity}`}
											</p>
											<p className="text-white font-semibold">
												{item.name || `Item ${globalIdx + 1}`}
											</p>
										</div>
										<div className="text-right">
											<p className={`text-3xl font-bold ${theme.priceText}`}>
												R${" "}
												{itemTotal.toLocaleString("pt-BR", {
													minimumFractionDigits: 2,
												})}
											</p>
											<p className="text-xs text-slate-500">/mes</p>
										</div>
									</div>
								</div>

								<div className="hidden lg:flex items-center justify-center">
									{item.image_url ? (
										<div className="relative w-full max-w-sm rounded-2xl border border-white/5 overflow-hidden shadow-2xl">
											<div
												className={`absolute inset-0 ${theme.glow} blur-xl opacity-20`}
											></div>
											<img
												src={item.image_url}
												alt={item.name}
												className="w-full h-auto object-cover relative"
											/>
										</div>
									) : (
										<div
											className={`relative w-72 h-72 rounded-3xl border border-white/5 ${theme.cardHover} bg-[#131620] flex items-center justify-center shadow-2xl transition-all`}
										>
											<div
												className={`absolute inset-0 ${theme.glow} rounded-3xl blur-xl opacity-20`}
											></div>
											<div
												className={`w-24 h-24 rounded-2xl ${theme.iconBg} flex items-center justify-center relative`}
											>
												<ItemIcon className={theme.icon} size={48} />
											</div>
										</div>
									)}
								</div>
							</div>
						</div>
					</section>
				);
			})}

			{compactItems.length > 0 && (
				<section className="py-24 bg-[#0F121C] border-y border-white/5 relative">
					<div className="max-w-6xl mx-auto px-6">
						{mainItems.length > 0 && (
							<div className="text-center mb-16">
								<h2 className="text-3xl font-bold text-white mb-4">
									Outros Servicos
								</h2>
								<p className="text-slate-400 max-w-2xl mx-auto">
									Itens adicionais contemplados nesta proposta.
								</p>
							</div>
						)}

						<div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
							{compactItems.map((item, idx) => {
								const globalIdx = items.indexOf(item);
								const theme = getItemTheme(item, globalIdx);
								const CompactIcon =
									ICON_MAP[item.icon] ||
									(item.item_type === "plan" ? Server : Box);
								return (
									<div
										key={idx}
										className={`bg-[#151926] p-8 rounded-2xl border border-white/5 ${theme.cardHover} transition-all group`}
									>
										<div
											className={`w-12 h-12 ${theme.iconBg} rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}
										>
											<CompactIcon className={theme.icon} />
										</div>
										<h3 className="text-xl font-bold text-white mb-2">
											{item.name || `Item ${globalIdx + 1}`}
										</h3>
										{item.description && (
											<p className="text-slate-400 text-sm leading-relaxed mb-4">
												{item.description}
											</p>
										)}
										<div className="flex items-center justify-between pt-4 border-t border-slate-800">
											<span className="text-xs text-slate-500 uppercase tracking-wider">
												{item.subtitle ||
													(item.item_type === "plan" ? "Plano" : "Modulo")}
												{item.quantity > 1 && ` x${item.quantity}`}
											</span>
											<span className={`text-lg font-bold ${theme.priceText}`}>
												R${" "}
												{(
													parseFloat(item.value) * (item.quantity || 1)
												).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
											</span>
										</div>
									</div>
								);
							})}
						</div>
					</div>
				</section>
			)}

			<section className="py-20 bg-gradient-to-b from-[#0B0F19] to-black border-t border-white/5">
				<div className="max-w-4xl mx-auto px-6">
					<div className="bg-[#131620] border border-slate-800 rounded-2xl p-8 md:p-12 shadow-2xl relative overflow-hidden">
						<div className="absolute top-0 right-0 w-64 h-64 bg-cyan-500/5 rounded-full blur-3xl -z-10"></div>
						<h2 className="text-2xl font-bold text-white mb-8 text-center">
							Resumo da Proposta
						</h2>

						<div className="space-y-4 mb-8">
							{items.map((item, i) => (
								<div
									key={i}
									className="flex justify-between items-center p-4 bg-[#0F1219] rounded-xl border border-slate-800"
								>
									<div className="flex items-center gap-4">
										<div
											className={`p-2 rounded ${item.item_type === "plan" ? "bg-purple-500/20 text-purple-400" : "bg-emerald-500/20 text-emerald-400"}`}
										>
											{item.item_type === "plan" ? (
												<Server size={20} />
											) : (
												<Box size={20} />
											)}
										</div>
										<div>
											<p className="text-white font-medium">
												{item.name || `Item ${i + 1}`}
											</p>
											<p className="text-xs text-slate-500">
												{item.item_type === "plan" ? "Plano" : "Modulo"}
												{item.quantity > 1 && ` - Qtd: ${item.quantity}`}
											</p>
										</div>
									</div>
									<div className="text-white font-bold">
										R${" "}
										{(
											parseFloat(item.value) * (item.quantity || 1)
										).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
									</div>
								</div>
							))}

							<div className="border-t border-slate-800 my-4"></div>
							<div className="flex justify-between items-end">
								<div className="text-slate-500 text-sm">
									<p>Cliente: {proposal.client_name}</p>
									{proposal.client_cnpj && <p>CNPJ: {proposal.client_cnpj}</p>}
								</div>
								<div className="text-right">
									<p className="text-sm text-slate-400 mb-1">Total Mensal</p>
									<p className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-white to-slate-400">
										R${" "}
										{total.toLocaleString("pt-BR", {
											minimumFractionDigits: 2,
										})}
									</p>
								</div>
							</div>
						</div>

						{proposal.notes && (
							<div className="mb-8 p-4 bg-slate-900/50 border border-slate-700 rounded-xl">
								<p className="text-xs text-slate-500 uppercase tracking-wider mb-2">
									Observacoes
								</p>
								<p className="text-slate-300 text-sm whitespace-pre-line">
									{proposal.notes}
								</p>
							</div>
						)}

						{!isResponded && !hideActions && (
							<div className="print-hide flex flex-col items-center gap-4">
								<button
									type="button"
									onClick={() => setShowAcceptModal(true)}
									className="w-full md:w-auto bg-white text-black hover:bg-slate-200 font-bold py-4 px-12 rounded-xl text-lg flex items-center justify-center gap-3 transition-all transform hover:scale-105 shadow-xl shadow-white/5"
								>
									Aprovar e Iniciar <ArrowRight size={20} />
								</button>
								<button
									type="button"
									onClick={() => setShowRejectModal(true)}
									className="text-sm text-slate-500 hover:text-red-400 transition-colors underline underline-offset-4"
								>
									Nao aceitar esta proposta
								</button>
								<p className="text-xs text-slate-600 flex items-center gap-2">
									<Lock size={12} /> Ambiente seguro.
								</p>
							</div>
						)}
					</div>
				</div>
			</section>

			<footer className="bg-black py-16 border-t border-slate-900">
				<div className="max-w-7xl mx-auto px-6 grid md:grid-cols-4 gap-8 md:gap-12">
					<div className="col-span-2">
						<div className="flex items-center gap-2 mb-4">
							{companyLogo ? (
								<img
									src={companyLogo}
									alt={companyName}
									className="h-8 object-contain"
								/>
							) : (
								<div className="h-8 w-8 bg-gradient-to-br from-cyan-600 to-purple-700 rounded flex items-center justify-center font-bold text-white text-xs">
									{companyName.charAt(0)}
								</div>
							)}
							<span className="text-xl font-bold text-white">
								{companyName}
							</span>
						</div>
						{companyDescription && (
							<p className="text-slate-500 text-sm mb-4 max-w-sm">
								{companyDescription}
							</p>
						)}
						<div className="text-xs text-slate-600 space-y-1">
							{proposal.company_legal_name && (
								<p>{proposal.company_legal_name}</p>
							)}
							{proposal.company_cnpj && <p>CNPJ: {proposal.company_cnpj}</p>}
						</div>
					</div>
					<div>
						<h4 className="text-white font-bold mb-4">Cliente</h4>
						<div className="text-sm text-slate-500 space-y-2">
							<p className="flex items-center gap-2">
								<Building2 size={14} /> {proposal.client_name}
							</p>
							{proposal.client_address && (
								<p className="flex items-center gap-2">
									<MapPin size={14} /> {proposal.client_address}
								</p>
							)}
							{proposal.client_phone && (
								<p className="flex items-center gap-2">
									<Phone size={14} /> {proposal.client_phone}
								</p>
							)}
							{proposal.client_email && (
								<p className="flex items-center gap-2">
									<Mail size={14} /> {proposal.client_email}
								</p>
							)}
						</div>
					</div>
					<div>
						<h4 className="text-white font-bold mb-4">Contato</h4>
						<div className="text-sm text-slate-500 space-y-2">
							{proposal.company_site && (
								<a
									href={`https://${proposal.company_site}`}
									className="hover:text-cyan-400 block transition-colors"
								>
									{proposal.company_site}
								</a>
							)}
							{proposal.company_email && (
								<a
									href={`mailto:${proposal.company_email}`}
									className="hover:text-cyan-400 block transition-colors"
								>
									{proposal.company_email}
								</a>
							)}
						</div>
					</div>
				</div>
				<div className="max-w-7xl mx-auto px-6 mt-12 pt-8 border-t border-slate-900 text-center text-xs text-slate-700">
					{new Date().getFullYear()} {companyName}. Proposta{" "}
					{proposal.proposal_number}.
				</div>
			</footer>
		</div>
	);
}
